import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ChevronRightIcon, CodeIcon, UsersIcon, BriefcaseIcon, StarIcon, TrendingUpIcon, ShieldCheckIcon, ZapIcon, AwardIcon, SearchIcon, ClockIcon, CheckCircle2Icon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { testimonials } from '../utils/data';
import { TechnologyLinksSection } from '../components/sections/TechnologyLinksSection';
const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState({
    stats: false
  });
  const statsRef = useRef<HTMLDivElement>(null);
  const slideTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Auto-advance slides
  useEffect(() => {
    const startTimer = () => {
      slideTimerRef.current = setTimeout(() => {
        handleSlideChange((activeSlide + 1) % 2);
      }, 6000); // 6 seconds per slide
    };
    startTimer();
    return () => {
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current);
      }
    };
  }, [activeSlide]);
  const handleSlideChange = (index: number) => {
    if (index === activeSlide || isTransitioning) return;
    setIsTransitioning(true);
    setActiveSlide(index);
    // Reset transitioning state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 800);
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target === statsRef.current) {
            setIsVisible(prev => ({
              ...prev,
              stats: true
            }));
          }
        }
      });
    }, {
      threshold: 0.2
    });
    if (statsRef.current) observer.observe(statsRef.current);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);
  // Stats
  const stats = [{
    number: '4,500+',
    label: 'Certified Developers',
    icon: AwardIcon
  }, {
    number: '50+',
    label: 'Hiring Companies',
    icon: BriefcaseIcon
  }, {
    number: '100+',
    label: 'Job Openings',
    icon: TrendingUpIcon
  }, {
    number: '15+',
    label: 'Certifications',
    icon: ShieldCheckIcon
  }];
  // Certification badges for animation
  const certificationBadges = [{
    name: 'Laravel Advanced',
    color: 'from-red-500 to-pink-500',
    delay: 0
  }, {
    name: 'Vue.js Pro',
    color: 'from-green-500 to-emerald-500',
    delay: 0.2
  }, {
    name: 'React Expert',
    color: 'from-blue-500 to-cyan-500',
    delay: 0.4
  }, {
    name: 'PHP Master',
    color: 'from-purple-500 to-indigo-500',
    delay: 0.6
  }];
  return <div className="w-full overflow-hidden" data-id="element-774">
      {/* Hero Slideshow Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-id="element-775">
        {/* Slide 1 - Developers */}
        <div className={`absolute inset-0 transition-opacity duration-800 ${activeSlide === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-776">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-777">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-778">
              <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#d73b3e]/10 to-transparent rounded-full blur-3xl" style={{
              transform: `translateY(${scrollY * 0.3}px)`
            }} data-id="element-779" />
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full blur-3xl" style={{
              transform: `translateY(${-scrollY * 0.2}px)`
            }} data-id="element-780" />
              {/* Grid pattern overlay */}
              <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                   linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }} data-id="element-781" />
            </div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 h-screen flex items-center" data-id="element-782">
            <div className="grid lg:grid-cols-2 gap-16 items-center w-full text-white" data-id="element-783">
              {/* Left content */}
              <div className={`transition-all duration-1000 ${activeSlide === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
              transitionDelay: activeSlide === 0 ? '200ms' : '0ms'
            }} data-id="element-784">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6" data-id="element-785">
                  <TrendingUpIcon className="w-4 h-4 text-[#d73b3e]" data-id="element-786" />
                  <span className="text-sm font-medium" data-id="element-787">
                    Join 4,500+ certified developers
                  </span>
                </div>

                <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-788">
                  Showcase your{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-789">
                    certified
                  </span>{' '}
                  and verifiable tech skills
                </h1>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-790">
                  Build your professional profile, display your certifications,
                  and connect with companies actively hiring. Stand out with
                  verified credentials.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-791">
                  <Link to="/signup" className="group px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center" data-id="element-792">
                    Create your FREE profile
                    <ChevronRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-793" />
                  </Link>
                  <Link to="/jobs" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20 flex items-center justify-center" data-id="element-794">
                    Browse Jobs
                  </Link>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-6" data-id="element-795">
                  <div data-id="element-796">
                    <div className="text-3xl font-bold mb-1" data-id="element-797">30%</div>
                    <div className="text-sm text-gray-400" data-id="element-798">Higher rates</div>
                  </div>
                  <div data-id="element-799">
                    <div className="text-3xl font-bold mb-1" data-id="element-800">7 days</div>
                    <div className="text-sm text-gray-400" data-id="element-801">Avg. to hired</div>
                  </div>
                  <div data-id="element-802">
                    <div className="text-3xl font-bold mb-1" data-id="element-803">100+</div>
                    <div className="text-sm text-gray-400" data-id="element-804">Active jobs</div>
                  </div>
                </div>
              </div>

              {/* Right visual - Video showcase */}
              <div className={`relative transition-all duration-1000 ${activeSlide === 0 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{
              transitionDelay: activeSlide === 0 ? '400ms' : '0ms'
            }} data-id="element-805">
                <div className="relative w-full max-w-lg mx-auto" data-id="element-806">
                  {/* Video Container */}
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 transform rotate-2 hover:rotate-0 transition-transform duration-500 bg-gray-900" data-id="element-807">
                    <div className="aspect-video w-full" data-id="element-808">
                      <video src="https://cdn.magicpatterns.com/uploads/hsKHrSfTuhHznpp9VrtCWc/Schermopname_2025-12-21_163931.undefined" autoPlay loop muted playsInline className="w-full h-full object-contain" data-id="element-809" />
                    </div>

                    {/* Overlay gradient for polish */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/20 to-transparent pointer-events-none" data-id="element-810" />
                  </div>

                  {/* Decorative elements behind */}
                  <div className="absolute -z-10 top-10 -right-10 w-full h-full bg-gradient-to-br from-[#d73b3e]/20 to-orange-500/20 rounded-2xl blur-xl transform rotate-6" data-id="element-811" />

                  {/* Floating badge */}
                  <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl flex items-center gap-3 animate-bounce" style={{
                  animationDuration: '3s'
                }} data-id="element-812">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center" data-id="element-813">
                      <CheckCircle2Icon className="w-6 h-6 text-green-600" data-id="element-814" />
                    </div>
                    <div data-id="element-815">
                      <div className="text-sm font-bold text-gray-900" data-id="element-816">
                        Verified
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-817">
                        Certificate Valid
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide 2 - Recruiters */}
        <div className={`absolute inset-0 transition-opacity duration-800 ${activeSlide === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-818">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-819">
            {/* Animated grid background */}
            <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-820">
              <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                   linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              transform: `translateY(${scrollY * 0.1}px)`
            }} data-id="element-821" />
            </div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 h-screen flex items-center" data-id="element-822">
            <div className="grid lg:grid-cols-2 gap-16 items-center w-full text-white" data-id="element-823">
              {/* Left visual - Technology logos showcase */}
              <div className={`relative transition-all duration-1000 ${activeSlide === 1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{
              transitionDelay: activeSlide === 1 ? '200ms' : '0ms'
            }} data-id="element-824">
                <div className="relative" data-id="element-825">
                  {/* Technology logos grid */}
                  <div className="grid grid-cols-3 gap-4 max-w-md" data-id="element-826">
                    {/* Row 1 */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.1s both' : 'none'
                  }} data-id="element-827">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/9a/Laravel.svg" alt="Laravel" className="w-12 h-12 object-contain" data-id="element-828" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.2s both' : 'none'
                  }} data-id="element-829">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg" alt="React" className="w-12 h-12 object-contain" data-id="element-830" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.3s both' : 'none'
                  }} data-id="element-831">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/95/Vue.js_Logo_2.svg" alt="Vue.js" className="w-12 h-12 object-contain" data-id="element-832" />
                    </div>

                    {/* Row 2 */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.4s both' : 'none'
                  }} data-id="element-833">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/2/27/PHP-logo.svg" alt="PHP" className="w-16 h-10 object-contain" data-id="element-834" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.5s both' : 'none'
                  }} data-id="element-835">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/4/4c/Typescript_logo_2020.svg" alt="TypeScript" className="w-12 h-12 object-contain" data-id="element-836" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.6s both' : 'none'
                  }} data-id="element-837">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/d/d9/Node.js_logo.svg" alt="Node.js" className="w-12 h-12 object-contain" data-id="element-838" />
                    </div>

                    {/* Row 3 */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.7s both' : 'none'
                  }} data-id="element-839">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/d/d5/Tailwind_CSS_Logo.svg" alt="Tailwind CSS" className="w-12 h-12 object-contain" data-id="element-840" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.8s both' : 'none'
                  }} data-id="element-841">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/3/3f/Git_icon.svg" alt="Git" className="w-12 h-12 object-contain" data-id="element-842" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.9s both' : 'none'
                  }} data-id="element-843">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg" alt="AWS" className="w-12 h-12 object-contain" data-id="element-844" />
                    </div>
                  </div>

                  {/* Floating stats badge */}
                  <div className="absolute -top-8 -right-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 shadow-2xl" data-id="element-845">
                    <div className="text-2xl font-bold" data-id="element-846">92%</div>
                    <div className="text-xs opacity-90" data-id="element-847">Match rate</div>
                  </div>

                  {/* Additional floating badge */}
                  <div className="absolute -bottom-6 left-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-3 shadow-2xl" data-id="element-848">
                    <div className="text-lg font-bold" data-id="element-849">4,500+</div>
                    <div className="text-xs opacity-90" data-id="element-850">Developers</div>
                  </div>
                </div>
              </div>

              {/* Right content */}
              <div className={`transition-all duration-1000 ${activeSlide === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
              transitionDelay: activeSlide === 1 ? '400ms' : '0ms'
            }} data-id="element-851">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6" data-id="element-852">
                  <ZapIcon className="w-4 h-4 text-[#d73b3e]" data-id="element-853" />
                  <span className="text-sm font-medium" data-id="element-854">
                    Trusted by 50+ companies
                  </span>
                </div>

                <h2 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-855">
                  Recruit the best{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-856">
                    tech talent
                  </span>{' '}
                  with verified certifications
                </h2>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-857">
                  Access 4,500+ certified developers with proven skills. Filter
                  by expertise, availability, and rate. Hire with confidence.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-858">
                  <Link to="/developers" className="group px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center" data-id="element-859">
                    Browse Certified Devs
                    <SearchIcon className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" data-id="element-860" />
                  </Link>
                  <Link to="/post-job" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20 flex items-center justify-center" data-id="element-861">
                    Post a job
                  </Link>
                </div>

                {/* Technology expertise callout */}
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10" data-id="element-862">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3" data-id="element-863">
                    Find experts in:
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-864">
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-865">
                      Laravel
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-866">
                      React
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-867">
                      Vue.js
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-868">
                      TypeScript
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-869">
                      Node.js
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-870">
                      +20 more
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Navigation Dots */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex gap-3" data-id="element-871">
          {[0, 1].map(index => <button key={index} onClick={() => handleSlideChange(index)} className={`transition-all duration-300 rounded-full ${activeSlide === index ? 'w-12 h-3 bg-[#d73b3e]' : 'w-3 h-3 bg-white/40 hover:bg-white/60'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-872" />)}
        </div>
      </section>

      {/* Technology Links Section */}
      <section className="bg-gray-50 py-16" data-id="element-873">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-874">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-875">
            Browse by Technology
          </h2>
          <TechnologyLinksSection data-id="element-876" />
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-white" data-id="element-877">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-878">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-879">
            {stats.map((stat, index) => {
            const Icon = stat.icon;
            return <div key={index} className={`text-center transition-all duration-700 ${isVisible.stats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
              transitionDelay: `${index * 100}ms`
            }} data-id="element-880">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl mb-4" data-id="element-881">
                    <Icon className="w-8 h-8 text-[#d73b3e]" data-id="element-882" />
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-883">
                    {stat.number}
                  </div>
                  <div className="text-sm font-medium text-gray-600" data-id="element-884">
                    {stat.label}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 bg-gradient-to-br from-[#FFF5F5] to-white" data-id="element-885">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-886">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-887">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-888">
              Why choose CertifiedDevs?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-889">
              The trusted platform for Laravel talent
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-890">
            <div className="bg-white p-8 rounded-2xl shadow-sm" data-id="element-891">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-892">
                <CodeIcon className="h-7 w-7 text-[#d73b3e]" data-id="element-893" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900" data-id="element-894">
                Vetted Developers
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-895">
                All developers are manually reviewed and verified to ensure
                quality.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm" data-id="element-896">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-897">
                <ShieldCheckIcon className="h-7 w-7 text-[#d73b3e]" data-id="element-898" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900" data-id="element-899">
                Certified Expertise
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-900">
                Industry-recognized certifications that validate real skills and
                knowledge.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm" data-id="element-901">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-902">
                <ZapIcon className="h-7 w-7 text-[#d73b3e]" data-id="element-903" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900" data-id="element-904">
                Fast Matching
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-905">
                Advanced filters and search to find the perfect match in days,
                not weeks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection testimonials={testimonials} data-id="element-906" />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white" data-id="element-907">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-908">
          <div className="max-w-3xl mx-auto text-center" data-id="element-909">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-910">
              Ready to get started?
            </h2>
            <p className="text-xl text-gray-300 mb-10" data-id="element-911">
              Join thousands of developers and companies already using
              CertifiedDevs
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-912">
              <Link to="/signup" className="px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-913">
                Get Certified
              </Link>
              <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20" data-id="element-914">
                Browse Developers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;
// Helper functions at the bottom of the file
const getFlagEmoji = (country: string) => {
  const countryFlags: {
    [key: string]: string;
  } = {
    'United States': '🇺🇸',
    Uganda: '🇺🇬',
    Germany: '🇩🇪',
    Philippines: '🇵🇭',
    Egypt: '🇪🇬',
    Italy: '🇮🇹',
    Romania: '🇷🇴',
    France: '🇫🇷',
    Spain: '🇪🇸',
    Nigeria: '🇳🇬',
    Pakistan: '🇵🇰',
    'United Kingdom': '🇬🇧',
    Switzerland: '🇨🇭',
    Indonesia: '🇮🇩',
    Turkey: '🇹🇷'
  };
  return <span className="text-lg" data-id="element-915">{countryFlags[country] || '🏳️'}</span>;
};
const getSkillIcon = (skill: string) => {
  const skillIcons: {
    [key: string]: string;
  } = {
    Laravel: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6raD3sQbYhrAYJrpu7Fmsg/Laravel.jpg',
    Python: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rmryh1szxRZK8Ckh29GLMA/python.png',
    React: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ptv5Wif4YhmkFBsFHL24ST/React.png',
    Symfony: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7M5FR1J9g2nFRdnfNT5c1d/Symfony.png',
    'Vue.js': 'https://uploadthingy.s3.us-west-1.amazonaws.com/ncaFrZrfpfapyV9ExA35DY/Vue_logo.png',
    // Fallback for other skills
    PHP: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/27/PHP-logo.svg/1280px-PHP-logo.svg.png',
    JavaScript: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/JavaScript-logo.png/800px-JavaScript-logo.png',
    TypeScript: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4c/Typescript_logo_2020.svg/1200px-Typescript_logo_2020.svg.png'
  };
  return skillIcons[skill] || 'https://via.placeholder.com/30?text=' + skill.charAt(0);
};
const getSkillColor = (skill: string) => {
  const skillColors: {
    [key: string]: string;
  } = {
    Laravel: '#FF3B30',
    PHP: '#8892BF',
    'Vue.js': '#42B883',
    React: '#61DAFB',
    JavaScript: '#F7DF1E',
    TypeScript: '#3178C6',
    MySQL: '#4479A1',
    'API Development': '#6DB33F',
    'Tailwind CSS': '#38B2AC',
    'Alpine.js': '#77C1D2',
    Livewire: '#FB70A9',
    Docker: '#2496ED',
    CSS: '#264DE4',
    HTML: '#E34F26'
  };
  return skillColors[skill] || '#CBD5E0';
};
<style jsx data-id="element-916">{`
  @keyframes spin-slow {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin-slow {
    animation: spin-slow 20s linear infinite;
  }
  @keyframes ping-slow {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.5);
      opacity: 0.4;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  }
  .animate-ping-slow {
    animation: ping-slow 3s ease-in-out infinite;
  }
  .animation-delay-500 {
    animation-delay: 0.5s;
  }
  .animation-delay-1000 {
    animation-delay: 1s;
  }
  .animation-delay-1500 {
    animation-delay: 1.5s;
  }
  svg line {
    stroke-dasharray: 5, 5;
    animation: dash 30s linear infinite;
  }
  @keyframes dash {
    to {
      stroke-dashoffset: 1000;
    }
  }
`}</style>;